import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import { OffboardingReturnGateway, ReturnRow, ClothingReturnBatch, RETURN_MODE } from './offboarding-return.gateway';
import { AssetActionModalComponent } from '../common/employee-assets/asset-action-modal.component';
import { ASSET } from '../common/employee-assets/asset.constants';
import { assetErrorKey, assetOperationId, assetToday, workflowTrackBy } from '../common/employee-assets/asset.helpers';

export interface ClothingReturnGroup { id: string; name: string; rows: ReturnRow[]; }

@Component({selector: 'app-offboarding-return-modal', templateUrl: './offboarding-return-modal.component.html',
  styleUrls: ['./offboarding-sequential.component.css', './offboarding-return-modal.component.css']})
export class OffboardingReturnModalComponent implements OnInit, OnDestroy {
  employeeId: string;
  mode: string;
  modes = RETURN_MODE;
  constants = ASSET;
  trackBy = workflowTrackBy;
  rows: ReturnRow[] = [];
  clothingGroups: ClothingReturnGroup[] = [];
  busy = false;
  errorKey = ASSET.empty;
  page = ASSET.zero;
  hasMore = false;
  loaded = false;
  clothingBlocked = false;
  refreshRequired = false;
  pending: ClothingReturnBatch[] = [];
  constructor(public modal: NgbActiveModal, private api: OffboardingReturnGateway, private modals: NgbModal) {}
  private destroyed = new Subject<void>();
  private dialog: NgbModalRef;
  private disposed = false;
  ngOnInit(): void { this.load(false); }
  ngOnDestroy(): void { this.disposed = true; this.closeDialog(); this.destroyed.next(); this.destroyed.complete(); }
  load(more: boolean, mutationError = ASSET.empty): void {
    if (this.disposed || this.busy) { return; }
    const page = more ? this.page + ASSET.one : ASSET.zero;
    this.busy = true; this.loaded = false;
    this.api.list(this.employeeId, page, this.mode).takeUntil(this.destroyed).subscribe(result => {
      this.rows = more ? this.rows.concat(result.items) : result.items; this.hasMore = result.hasMore; this.page = page;
      this.groupClothing();
      this.busy = false; this.loaded = true;
      this.clothingBlocked = this.rows.some(row => row.unit && !row.unit.canReturn);
      if (!this.pending.length) { this.errorKey = mutationError; }
    }, error => {
      this.busy = false; this.errorKey = assetErrorKey(error, false);
      if (!more) { this.rows = []; this.groupClothing(); }
    });
  }
  private groupClothing(): void {
    this.clothingGroups = [];
    this.rows.forEach(row => {
      if (!row.unit) { return; }
      let group = this.clothingGroups.filter(value => value.id === row.unit.catalogItemId)[ASSET.zero];
      if (!group) { group = {id: row.unit.catalogItemId, name: row.name, rows: []}; this.clothingGroups.push(group); }
      group.rows.push(row);
    });
  }
  receive(row: ReturnRow): void {
    if (this.disposed || this.dialog || this.busy || !row.asset) { return; }
    this.receiveAsset(row);
  }
  receiveAll(): void {
    if (this.disposed || this.busy || !this.loaded || this.hasMore || this.clothingBlocked && !this.pending.length
      || this.errorKey === ASSET.deniedKey || this.mode !== RETURN_MODE.clothing) { return; }
    if (!this.pending.length) { this.prepareClothing(); }
    if (!this.pending.length) { return; }
    this.busy = true; this.errorKey = ASSET.empty; this.receiveNext();
  }
  private prepareClothing(): void {
    this.rows.forEach(row => {
      let batch = this.pending.filter(value => value.catalogItemId === row.unit.catalogItemId)[ASSET.zero];
      if (!batch) {
        batch = {catalogItemId: row.unit.catalogItemId, request: {operationId: assetOperationId(), effectiveDate: assetToday(), units: []}};
        this.pending.push(batch);
      }
      batch.request.units.push({unitId: row.unit.unitId, version: row.unit.version});
    });
  }
  private receiveNext(): void {
    this.api.returnClothing(this.employeeId, this.pending[ASSET.zero]).takeUntil(this.destroyed).subscribe(() => {
      this.refreshRequired = true;
      this.pending.shift();
      if (this.pending.length) { this.receiveNext(); } else { this.busy = false; this.modal.close(true); }
    }, error => {
      this.busy = false; this.errorKey = assetErrorKey(error, true);
      this.refreshRequired = this.refreshRequired || this.errorKey === ASSET.unknownKey;
      if (this.errorKey !== ASSET.unknownKey) { this.pending = []; }
      this.load(false, this.errorKey);
    });
  }
  private receiveAsset(row: ReturnRow): void {
    const ref = this.modals.open(AssetActionModalComponent, {size: ASSET.modalSize, beforeDismiss: () => !ref.componentInstance.busy});
    ref.componentInstance.asset = row.asset; ref.componentInstance.action = row.asset.kind === ASSET.service ? ASSET.revokeAccess : ASSET.returnAction;
    this.owns(ref);
  }
  private owns(ref: NgbModalRef): void {
    const content: AssetActionModalComponent = ref.componentInstance;
    this.dialog = ref; ref.result.then(() => this.returned(ref), () => this.cancelled(ref, content));
  }
  private returned(ref: NgbModalRef): void {
    if (this.dialog !== ref) { return; }
    this.dialog = null;
    if (!this.disposed) { this.refreshRequired = true; this.load(false); }
  }
  private cancelled(ref: NgbModalRef, content: AssetActionModalComponent): void {
    if (this.dialog !== ref) { return; }
    if (content.errorKey === ASSET.unknownKey || content.errorKey === ASSET.conflictKey || content.refreshed) { this.returned(ref); } else { this.dialog = null; }
  }
  private closeDialog(): void { if (this.dialog) { const ref = this.dialog; this.dialog = null; ref.close(); } }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/offboarding/offboarding-return-modal.component.ts